/* eslint-disable camelcase */
import type { EmojiReactions, CommentReaction } from '~/graphql';
import { isNil } from '~/utilities/type-guards';

export type GroupedReaction = {
  currentContactReacted: boolean;
  reactions: CommentReaction[];
};

export const MAX_NAMES_FOR_LABEL = 3;

export const REACTIONS: Record<EmojiReactions, number> = {
  thumbs_up: 0x1f44d,
  thumbs_down: 0x1f44e,
  plus: 0x2795,
  laugh: 0x1f602,
  party_popper: 0x1f389,
};

export const generateEmojiString = (emoji: EmojiReactions): string => {
  return String.fromCodePoint(REACTIONS[emoji]);
};

export const groupReactionsByEmoji = (
  commentReactions: CommentReaction[],
): Map<EmojiReactions, GroupedReaction> => {
  const reactionMap = new Map<EmojiReactions, GroupedReaction>();

  commentReactions.forEach((reaction) => {
    const { emoji, belongsToCurrentContact } = reaction;

    const mappedReaction = reactionMap.get(emoji);

    if (isNil(mappedReaction)) {
      reactionMap.set(emoji, {
        currentContactReacted: belongsToCurrentContact,
        reactions: [reaction],
      });
    } else {
      const { currentContactReacted, reactions } = mappedReaction;

      reactionMap.set(emoji, {
        currentContactReacted: currentContactReacted || belongsToCurrentContact,
        reactions: belongsToCurrentContact ? [reaction, ...reactions] : [...reactions, reaction],
      });
    }
  });

  return reactionMap;
};

export const generateReactionLabel = (reactions: CommentReaction[]): string => {
  const numReactions = reactions.length;

  const namesForLabel = reactions.slice(0, MAX_NAMES_FOR_LABEL).map((reaction) => {
    if (reaction.belongsToCurrentContact) {
      return 'You';
    }

    return reaction.contactName ?? 'Unknown';
  });

  if (numReactions === 1) {
    return namesForLabel[0];
  }
  if (numReactions <= MAX_NAMES_FOR_LABEL) {
    const beginningNames = namesForLabel.slice(0, -1);
    const finalName = namesForLabel.slice(-1)[0];
    return `${beginningNames.join(', ')}${beginningNames.length > 1 ? ',' : ''} and ${finalName}`;
  }
  const numOthers = numReactions - MAX_NAMES_FOR_LABEL;
  return `${namesForLabel.join(', ')}, and ${numOthers} other${numOthers > 1 ? 's' : ''}`;
};
